import { ReactNode, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import { Container, Spinner } from "reactstrap";
import Breadcrumb from "Components/organisms/breadcrumb";
import ActionButton from "Components/atoms/buttons/action-button";
import PrimaryButton from "Components/atoms/buttons/primary-button";
import SelectInput from "Components/molecule/inputs/select-input";
import TextInput from "Components/molecule/inputs/text-input";
import InfoTooltip from "Components/molecule/info-tooltip";
import {
    HrTermination,
    HrTerminationReason,
    TURNOVER_FORMULA_LABELS,
    TurnoverFormula,
} from "models/hr-dashboard";
import Screen, { SCREENS } from "models/screen";
import FiscalCalendar from "utils/fiscal-calendar";
import { PermissionsSliceState } from "slices/auth/permissions/reducer";
import {
    getHrDashboardConfig,
    getHrTerminationReasons,
    getHrTerminations,
    setHrReasonMapping,
    setHrTerminationOverride,
    updateHrDashboardConfig,
} from "slices/human-resources/hr-dashboard/thunk";

const selectPermissions = createSelector(
    (state: any) => state.Permissions,
    ({ screens }: PermissionsSliceState) => ({ screens })
);

// <select> values for a nullable boolean
const toOption = (v: boolean | null) => (v === null ? "" : v ? "yes" : "no");
const fromOption = (v: string): boolean | null => (v === "" ? null : v === "yes");
const regrettableLabel = (v: boolean | null) => (v === null ? "Unclassified" : v ? "Regrettable" : "Non-regrettable");

const formatDate = (iso: string | null) => (iso ? iso.slice(0, 10) : "—");

// Input label with an info icon (the shared inputs only take a plain text label)
const LabeledField = ({ label, info, children }: { label: string; info: ReactNode; children: ReactNode }) => (
    <div className="d-flex flex-column">
        <span className="input-label">{label}<InfoTooltip>{info}</InfoTooltip></span>
        {children}
    </div>
);

const HRDashboardSettingsPage = () => {
    document.title = "HR Dashboard Settings | IbexIQ";

    const navigate = useNavigate();
    const { screens } = useSelector(selectPermissions);
    const isHrAdmin = useMemo(() => screens.some((s: Screen) => s.code === SCREENS.HR_ADMIN), [screens]);

    // Settings form (threshold edited as a percentage)
    const [formula, setFormula] = useState<TurnoverFormula>("RAM_EXPOSURE");
    const [maxPercent, setMaxPercent] = useState("3.3");
    const [smallGroupMin, setSmallGroupMin] = useState("3");
    const [isSaving, setIsSaving] = useState(false);

    const [reasons, setReasons] = useState<HrTerminationReason[]>([]);
    const [fiscalYear, setFiscalYear] = useState(FiscalCalendar.currentFiscalYear());
    const [terminations, setTerminations] = useState<HrTermination[]>([]);
    const [isLoadingTerminations, setIsLoadingTerminations] = useState(true);
    const [isLoading, setIsLoading] = useState(true);

    const fiscalYearOptions = useMemo(() => {
        const current = FiscalCalendar.currentFiscalYear();
        return Array.from({ length: 5 }, (_, i) => current - i).map(fy => ({ value: String(fy), label: `FY${fy}` }));
    }, []);

    const loadTerminations = async (fy: number) => {
        setIsLoadingTerminations(true);
        const data = await getHrTerminations(fy);
        setTerminations(data ?? []);
        setIsLoadingTerminations(false);
    };

    useEffect(() => {
        if (!isHrAdmin) return;
        const init = async () => {
            setIsLoading(true);
            const [config, reasonList] = await Promise.all([getHrDashboardConfig(), getHrTerminationReasons()]);
            if (config) {
                setFormula(config.turnoverFormula);
                setMaxPercent(String(Math.round(config.turnoverQuarterMax * 1000) / 10));
                setSmallGroupMin(String(config.smallGroupMin));
            }
            setReasons(reasonList ?? []);
            setIsLoading(false);
        };
        init();
    }, [isHrAdmin]);

    useEffect(() => {
        if (isHrAdmin) loadTerminations(fiscalYear);
    }, [isHrAdmin, fiscalYear]);

    const handleSaveConfig = async () => {
        setIsSaving(true);
        await updateHrDashboardConfig({
            turnoverFormula: formula,
            turnoverQuarterMax: Number(maxPercent) / 100,
            smallGroupMin: Number(smallGroupMin),
        });
        setIsSaving(false);
    };

    const handleReasonChange = async (reason: string, value: string) => {
        const updated = await setHrReasonMapping(reason, fromOption(value));
        if (updated) {
            setReasons(updated);
            await loadTerminations(fiscalYear); // the mapping changes the effective value of each termination
        }
    };

    const handleOverrideChange = async (employeeId: string, value: string) => {
        const ok = await setHrTerminationOverride(employeeId, fromOption(value));
        if (ok) await loadTerminations(fiscalYear);
    };

    const actions = <ActionButton onClick={() => navigate("/human-resources/dashboard")}>
        <i className="bx bx-arrow-back" /> Back to dashboard
    </ActionButton>;

    if (!isHrAdmin) {
        return <div className="page-content">
            <Container fluid style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <Breadcrumb title="HR Dashboard" breadcrumbItem="Settings" actions={actions} />
                <div className="section-container">
                    <p className="text-muted mb-0">Only Human Resources Admins can change the HR Dashboard settings.</p>
                </div>
            </Container>
        </div>;
    }

    return <div className="page-content">
        <Container fluid style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Breadcrumb title="HR Dashboard" breadcrumbItem="Settings" actions={actions} />

            {isLoading ? <div className="section-container text-center py-5"><Spinner /></div> : <>
                <div className="section-container">
                    <h5 className="mb-3">
                        Turnover and privacy
                        <InfoTooltip>
                            How the dashboard calculates turnover, when a quarter is flagged and which small groups are hidden.
                            Changes apply to every user the next time they open the dashboard.
                        </InfoTooltip>
                    </h5>
                    <div className="d-flex flex-wrap gap-3 align-items-end">
                        <LabeledField
                            label="Turnover formula"
                            info={<>
                                <b>RAM formula</b> (HR's Excel): people who left ÷ (headcount at the start of the period + people hired in the period).<br />
                                <b>Standard</b>: people who left ÷ average headcount ((start + end) ÷ 2).<br />
                                Used in Employee Turnover and Retention.
                            </>}
                        >
                            <SelectInput
                                options={Object.entries(TURNOVER_FORMULA_LABELS).map(([value, label]) => ({ value, label }))}
                                value={formula}
                                onChange={e => setFormula(e.target.value as TurnoverFormula)}
                            />
                        </LabeledField>
                        <LabeledField
                            label="Maximum quarter turnover (%)"
                            info={<>
                                Target limit per quarter. In Employee Turnover, a quarter above this value shows in red; at or below it, in green.
                                Example: 3.3 means 3.3% in the quarter.
                            </>}
                        >
                            <TextInput
                                type="number"
                                step="0.1"
                                min="0"
                                max="100"
                                value={maxPercent}
                                onChange={e => setMaxPercent(e.target.value)}
                            />
                        </LabeledField>
                        <LabeledField
                            label="Minimum group size (privacy)"
                            info={<>
                                Protects people's privacy in Headcount by Department and Retention. A department with fewer people than
                                this number (headcount at the start of the FY + hires) is not shown on its own: it is combined with others as
                                "Other (combined for privacy)" or hidden. HR Admins always see everything. Totals are always shown.
                            </>}
                        >
                            <TextInput
                                type="number"
                                step="1"
                                min="0"
                                value={smallGroupMin}
                                onChange={e => setSmallGroupMin(e.target.value)}
                            />
                        </LabeledField>
                        <PrimaryButton onClick={handleSaveConfig} disabled={isSaving}>
                            {isSaving ? "Saving..." : "Save"}
                        </PrimaryButton>
                    </div>
                </div>

                <div className="section-container">
                    <h5 className="mb-1">
                        Termination reasons
                        <InfoTooltip>
                            Each reason comes from the employee's termination in Dayforce. Choose whether leaving for that reason
                            is <b>Regrettable</b> (we wanted to keep the person, e.g. resigned for another job) or <b>Non-regrettable</b> (e.g.
                            end of contract or dismissal). This splits Employee Turnover into regrettable and non-regrettable; reasons that
                            are not mapped count as Unclassified. "Employees" is how many terminated employees have that reason.
                        </InfoTooltip>
                    </h5>
                    <p className="small text-muted">
                        Reasons come from Dayforce. Map each one to regrettable or non-regrettable; departures with an unmapped reason show as Unclassified.
                    </p>
                    {reasons.length === 0 ? <p className="text-muted mb-0">No termination reasons found.</p> : (
                        <div className="table-responsive">
                            <table className="table table-sm table-bordered mb-0">
                                <thead>
                                    <tr>
                                        <th>Reason</th>
                                        <th className="text-end">Employees</th>
                                        <th style={{ width: 220 }}>Classification</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reasons.map(r => (
                                        <tr key={r.reason}>
                                            <td>{r.reason}</td>
                                            <td className="text-end">{r.count}</td>
                                            <td>
                                                <select className="form-select form-select-sm" value={toOption(r.regrettable)}
                                                    onChange={e => handleReasonChange(r.reason, e.target.value)}>
                                                    <option value="">Not mapped</option>
                                                    <option value="yes">Regrettable</option>
                                                    <option value="no">Non-regrettable</option>
                                                </select>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="section-container">
                    <div className="d-flex flex-wrap justify-content-between align-items-end mb-2">
                        <div>
                            <h5 className="mb-1">
                                Departures
                                <InfoTooltip>
                                    Every employee who left in the selected fiscal year. "From mapping" is the classification that comes from
                                    their reason (table above). Use <b>Override</b> when one person's case is different from their reason, e.g. a
                                    resignation that was not regretted. The override wins over the mapping; "Use reason mapping" removes it.
                                </InfoTooltip>
                            </h5>
                            <p className="small text-muted mb-0">Override the classification of a single departure. "Use reason mapping" removes the override.</p>
                        </div>
                        <SelectInput
                            options={fiscalYearOptions}
                            value={String(fiscalYear)}
                            onChange={e => setFiscalYear(Number(e.target.value))}
                            disabled={isLoadingTerminations}
                        />
                    </div>
                    {isLoadingTerminations ? <div className="text-center py-4"><Spinner /></div> :
                    terminations.length === 0 ? <p className="text-muted mb-0">No departures in FY{fiscalYear}.</p> : (
                        <div className="table-responsive">
                            <table className="table table-sm table-bordered mb-0">
                                <thead>
                                    <tr>
                                        <th>Employee</th>
                                        <th>Department</th>
                                        <th>Hire date</th>
                                        <th>Termination date</th>
                                        <th>Reason</th>
                                        <th>From mapping</th>
                                        <th style={{ width: 220 }}>Override</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {terminations.map(t => (
                                        <tr key={t.employeeId}>
                                            <td>{t.name}</td>
                                            <td>{t.department ?? "—"}</td>
                                            <td>{formatDate(t.hireDate)}</td>
                                            <td>{formatDate(t.terminatedAt)}</td>
                                            <td>{t.reason ?? "—"}</td>
                                            <td>{regrettableLabel(t.mappedRegrettable)}</td>
                                            <td>
                                                <select className="form-select form-select-sm" value={toOption(t.overrideRegrettable)}
                                                    onChange={e => handleOverrideChange(t.employeeId, e.target.value)}>
                                                    <option value="">Use reason mapping</option>
                                                    <option value="yes">Regrettable</option>
                                                    <option value="no">Non-regrettable</option>
                                                </select>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </>}
        </Container>
    </div>;
};

export default HRDashboardSettingsPage;
